import React from "react";
import Navigation from "../components/site/Navigation";
import Footer from "../components/site/Footer";
import MagneticButton from "../components/site/ui/MagneticButton";
import Reveal from "../components/site/Reveal";
import WhatThisMovementIsAbout from "../components/site/WhatThisMovementIsAbout";
import OrientationResetCheckoutHost from "../components/site/OrientationResetCheckoutHost";
import PageSeo from "../components/site/PageSeo";
import { SITE_URL, ORGANIZATION, PERSON } from "../data/entityGraph";
import { trackEvent } from "../lib/track";
import { navigateToSection } from "../components/site/navigation/navConfig";

const LOGO = "/assets/logo.png";
const PORTRAIT_SRC = "/assets/brand/cyrus-portrait.webp";

const PortraitFrame = () => (
  <div className="relative w-full max-w-sm mx-auto">
    {/* Champagne bloom */}
    <div
      className="absolute -inset-6 rounded-[28px] blur-2xl opacity-60 pointer-events-none"
      style={{ background: "radial-gradient(circle, rgba(212,175,55,0.35), transparent 70%)" }}
      aria-hidden
    />
    {/* Inner gold ring */}
    <div className="relative rounded-[22px] p-[1.5px] bg-gradient-to-br from-[#F3E5AB] via-[#D4AF37] to-[#7A5F12]">
      <div className="rounded-[20px] overflow-hidden bg-black/40">
        <img
          src={PORTRAIT_SRC}
          alt="Cyrus Prescod"
          data-testid="about-portrait"
          className="block w-full aspect-[4/5] object-cover"
          draggable="false"
          loading="lazy"
          decoding="async"
          onError={(e) => {
            // Graceful fallback if the file isn't deployed yet.
            e.currentTarget.style.display = "none";
            const sibling = e.currentTarget.nextElementSibling;
            if (sibling) sibling.style.display = "flex";
          }}
        />
        <div
          className="hidden w-full aspect-[4/5] flex-col items-center justify-center text-center px-6"
          style={{ background: "linear-gradient(180deg, #1a1208 0%, #0a0703 100%)" }}
          data-testid="about-portrait-placeholder"
        >
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-3">
            Portrait-Placeholder
          </div>
          <div className="font-serif text-2xl text-themed">CYRUS PRESCOD · FOUNDER</div>
        </div>
      </div>
    </div>
    <p className="mt-5 text-center text-[10px] tracking-[0.4em] uppercase text-gold/80">
      CYRUS PRESCOD · FOUNDER
    </p>
  </div>
);

// Reusable long-form prose block. Constrains line-length (3xl), uses
// relaxed leading and stronger contrast (text-white/85) so reading
// the About page does not fatigue the eye on either dark or light
// theme. Paragraphs are kept to ~3-line blocks in the calling JSX.
const Section = ({ children }) => (
  <p className="max-w-3xl text-white/85 leading-relaxed text-[16px] md:text-[17.5px]">
    {children}
  </p>
);

const AboutPage = () => {
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "About", item: `${SITE_URL}/about` },
    ],
  };

  return (
    <>
      <PageSeo
        title="About Authentic Momentum | The Origin of the Movement"
        description="The story behind Authentic Momentum. How Cyrus Prescod built a framework for moving from force into flow from lived experience, not theory."
        canonical={`${SITE_URL}/about`}
        jsonLd={[
          { "@context": "https://schema.org", ...PERSON },
          { "@context": "https://schema.org", ...ORGANIZATION },
          breadcrumbLd,
        ]}
      />
      <Navigation />
      <main className="relative pt-32 pb-32 noise-overlay min-h-screen">
        <div className="absolute inset-0 gold-glow-radial pointer-events-none" aria-hidden />

        {/* HERO */}
        <section className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <div className="flex justify-center mb-10">
              <img
                src={LOGO}
                alt="Authentic Momentum"
                className="w-20 h-20 object-contain logo-mask animate-breathe-premium"
                draggable="false"
              />
            </div>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
              The Person Behind the Method
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl text-themed leading-tight tracking-tight font-light">
              Cyrus <span className="italic gold-gradient-text">Prescod.</span>
            </h1>
            <p className="mt-8 text-themed-muted max-w-2xl mx-auto leading-relaxed">
              Strategist, author, and creative systems architect helping people
              turn internal clarity into visible digital momentum.
            </p>
          </Reveal>
        </section>

        {/* PORTRAIT + INTRO COPY */}
        <section className="relative max-w-6xl mx-auto px-6 md:px-10 mt-20 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-5">
            <PortraitFrame />
          </Reveal>

          <Reveal delay={150} className="lg:col-span-7 space-y-6">
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold">
              About the Author and the Authentic Momentum Movement
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl text-themed leading-tight font-light">
              A movement built from{" "}
              <span className="italic gold-gradient-text">lived experience.</span>
            </h2>
            <Section>
              Authentic Momentum was created by Cyrus Prescod, an author,
              systems thinker, and developer of transformational frameworks
              designed to help people move from force, confusion, and survival
              mode into clarity, alignment, and purposeful action.
            </Section>
            <Section>
              Cyrus built Authentic Momentum from lived experience. This is not
              a theory created from the outside looking in. It is a system born
              from real struggle, observation, personal refinement, and the
              deep understanding that most people are not lacking potential.
              They are lacking alignment, structure, language, and a system
              that helps them access what is already within them.
            </Section>
          </Reveal>
        </section>

        {/* DEEPER COPY */}
        <section className="relative max-w-3xl mx-auto px-6 md:px-10 mt-20 space-y-8">
          <Reveal>
            <Section>
              At its core, Authentic Momentum is a movement for people who know
              they are meant for more, but are tired of forcing their way
              through life without clarity. It speaks to the person who has
              ambition, vision, creativity, and inner depth, but needs a new
              way to organize their energy, make decisions, and move forward
              without betraying who they truly are.
            </Section>
          </Reveal>

          <Reveal delay={100}>
            <div className="glass-card p-10 my-10">
              <div className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-4 text-center">
                The Core Idea
              </div>
              <p className="font-serif italic text-2xl sm:text-3xl text-themed leading-snug text-center">
                "When you stop moving from pressure and start moving from
                truth, momentum becomes natural."
              </p>
            </div>
          </Reveal>

          {/* What This Movement Is Really About — sourced from the
              shared component so Home and About stay in lockstep. */}
          <WhatThisMovementIsAbout id="about-movement" />

          <Reveal delay={250}>
            <p className="text-[11px] tracking-[0.4em] uppercase text-gold mb-4 mt-4">
              The Logic of the Movement
            </p>
            <ul className="space-y-4 text-themed-muted leading-relaxed">
              <li className="flex gap-4">
                <span className="font-serif text-gold text-2xl leading-none">1.</span>
                <span>It captures attention through emotional truth.</span>
              </li>
              <li className="flex gap-4">
                <span className="font-serif text-gold text-2xl leading-none">2.</span>
                <span>It creates resonance by naming what the audience is experiencing.</span>
              </li>
              <li className="flex gap-4">
                <span className="font-serif text-gold text-2xl leading-none">3.</span>
                <span>
                  It introduces Authentic Momentum as the bridge between where
                  they are and where they know they are meant to be.
                </span>
              </li>
              <li className="flex gap-4">
                <span className="font-serif text-gold text-2xl leading-none">4.</span>
                <span>
                  It invites them into a system, a philosophy, and a movement
                  that can help them move forward with clarity, confidence,
                  and purpose.
                </span>
              </li>
            </ul>
          </Reveal>

          <Reveal delay={300}>
            <Section>
              Authentic Momentum is more than a book, brand, or personal
              development concept. It is a living framework for transformation.
              It is for the builders, creatives, leaders, seekers, and
              visionaries who are ready to stop forcing a version of life that
              no longer fits and begin moving in rhythm with who they truly
              are becoming.
            </Section>
          </Reveal>

          <Reveal delay={350}>
            <div className="text-center pt-6 space-y-2">
              <p className="font-serif italic text-themed text-xl">
                This is the work Cyrus Prescod is building.
              </p>
              <p className="font-serif italic text-themed text-xl">
                This is the system behind the movement.
              </p>
              <p className="font-serif italic text-2xl gold-gradient-text">
                This is Authentic Momentum.
              </p>
            </div>
          </Reveal>
        </section>

        {/* CTAs */}
        <section className="relative max-w-4xl mx-auto px-6 md:px-10 mt-20">
          <Reveal>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <MagneticButton
                data-testid="about-cta-blueprint"
                onClick={() => navigateToSection("blueprint")}
                className="btn-gold"
              >
                Start Your Momentum Personal Action Plan
              </MagneticButton>
              <button
                type="button"
                data-testid="about-cta-orientation-reset"
                onClick={() => {
                  trackEvent("about_orientation_reset_cta_clicked");
                  window.dispatchEvent(
                    new CustomEvent("am:open-orientation-reset-checkout")
                  );
                }}
                className="btn-ghost-gold"
              >
                Book the Clarity Reset Session · $149
              </button>
            </div>
          </Reveal>
        </section>

        {/* Entity bridge - cross-link to /cyrus-prescod, /framework, /press */}
        <section className="relative max-w-4xl mx-auto px-6 md:px-10 mt-20" data-testid="about-entity-bridge">
          <Reveal>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
              Go Deeper
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a
                href="/cyrus-prescod"
                data-testid="about-bridge-cyrus"
                className="btn-ghost-gold inline-flex"
                onClick={() => trackEvent("about_to_cyrus_profile_clicked")}
              >
                Founder Profile
              </a>
              <a
                href="/framework"
                data-testid="about-bridge-framework"
                className="btn-ghost-gold inline-flex"
                onClick={() => trackEvent("about_to_framework_clicked")}
              >
                The Framework
              </a>
              <a
                href="/press"
                data-testid="about-bridge-press"
                className="btn-ghost-gold inline-flex"
                onClick={() => trackEvent("about_to_press_clicked")}
              >
                Press
              </a>
            </div>
          </Reveal>
        </section>
      </main>
      <Footer />
      <OrientationResetCheckoutHost />
    </>
  );
};

export default AboutPage;
